import * as Permissions from 'expo-permissions';
import { SignupScreen, userAvatarConfig } from 'fitbeat.gym-core';
import { Platform } from 'react-native';
import {
  CameraOptions,
  ImageLibraryOptions,
  ImagePickerResponse,
  launchCamera,
  launchImageLibrary,
} from 'react-native-image-picker';
import { IAppStore } from '../../HomeWorkouts';
import { MemoryImageCache } from '../../services';
import { showMessage } from '../../ui';
import { i18n } from '../../utils';
import { createUserServiceClient } from '../../utils/apiClientFactory';
import { Logger } from '../../utils/Logger';
import { getNextStep, navigateToNextStep } from '../actions';

export const ADD_AVATAR_SCREEN_MOUNTED_REQUEST = 'ADD_AVATAR_SCREEN_MOUNTED_REQUEST';
export const ADD_AVATAR_SCREEN_MOUNTED_SUCCESS = 'ADD_AVATAR_SCREEN_MOUNTED_SUCCESS';
export const ADD_AVATAR_SCREEN_MOUNTED_FAILURE = 'ADD_AVATAR_SCREEN_MOUNTED_FAILURE';

export const ADD_AVATAR_REQUEST = 'ADD_AVATAR_REQUEST';
export const ADD_AVATAR_SUCCESS = 'ADD_AVATAR_SUCCESS';
export const ADD_AVATAR_FAILURE = 'ADD_AVATAR_FAILURE';

export const ADD_AVATAR_NEXT_PRESSED_SUCCESS = 'ADD_AVATAR_NEXT_PRESSED_SUCCESS';
export const ADD_AVATAR_NEXT_PRESSED_REQUEST = 'ADD_AVATAR_NEXT_PRESSED_REQUEST';
export const ADD_AVATAR_NEXT_PRESSED_FAILURE = 'ADD_AVATAR_NEXT_PRESSED_FAILURE';

export const SAVE_AVATAR_PRESS_REQUEST = 'SAVE_AVATAR_PRESS_REQUEST';
export const SAVE_AVATAR_PRESS_SUCCESS = 'SAVE_AVATAR_PRESS_SUCCESS';
export const SAVE_AVATAR_PRESS_FAILURE = 'SAVE_AVATAR_PRESS_FAILURE';

export const ADD_AVATAR_UNMOUNTED = 'ADD_AVATAR_UNMOUNTED';

const getBase64ImageFromCameraResponse = (response: ImagePickerResponse): string | undefined => {
  if (response.didCancel) {
    return;
  }

  const base64result = response.assets?.find((asset) => asset.base64);
  if (!base64result?.base64) {
    throw new Error('Expected base46 encoded file');
  }

  return base64result.base64;
};

export interface IAddAvatarRequest {
  type: typeof ADD_AVATAR_REQUEST;
}

export interface IAddAvatarSuccess {
  type: typeof ADD_AVATAR_SUCCESS;
  payload: {
    uploadedAvatar?: string;
  };
}

export interface IAddAvatarFailure {
  type: typeof ADD_AVATAR_FAILURE;
  payload: {
    errorMessage: string;
  };
}

export interface IAddAvatarNextPressedRequest {
  type: typeof ADD_AVATAR_NEXT_PRESSED_REQUEST;
}

export interface IAddAvatarNextPressedSuccess {
  type: typeof ADD_AVATAR_NEXT_PRESSED_SUCCESS;
}

export interface IAddAvatarNextPressedFailure {
  type: typeof ADD_AVATAR_NEXT_PRESSED_FAILURE;
  payload: {
    errorMessage: string;
  };
}

export interface IAddAvatarScreenMountedRequest {
  type: typeof ADD_AVATAR_SCREEN_MOUNTED_REQUEST;
}

export interface IAddAvatarScreenMountedSuccess {
  type: typeof ADD_AVATAR_SCREEN_MOUNTED_SUCCESS;
  payload: {
    existingAvatar?: string;
  };
}

export interface IAddAvatarScreenMountedFailure {
  type: typeof ADD_AVATAR_SCREEN_MOUNTED_FAILURE;
  payload: {
    errorMessage: string;
  };
}

export interface ISaveAvatarPressRequest {
  type: typeof SAVE_AVATAR_PRESS_REQUEST;
}

export interface ISaveAvatarPressSuccess {
  type: typeof SAVE_AVATAR_PRESS_SUCCESS;
  payload: {
    existingAvatar?: string;
  };
}

export interface ISaveAvatarPressFailure {
  type: typeof SAVE_AVATAR_PRESS_FAILURE;
  payload: {
    errorMessage: string;
  };
}

export interface IAddAvatarUnMounted {
  type: typeof ADD_AVATAR_UNMOUNTED;
}

const imagePickerOptions: ImageLibraryOptions = {
  mediaType: 'photo',
  includeBase64: true,
  quality: 0.4,
};

const cameraOptions: CameraOptions = {
  mediaType: 'photo',
  includeBase64: true,
  maxWidth: userAvatarConfig.dimensions.width,
  maxHeight: userAvatarConfig.dimensions.height,
  quality: 0.4,
  cameraType: 'front',
};

export const onboardingCaptureAvatarDidMount = (userId: string) => {
  return async (dispatch) => {
    try {
      dispatch({ type: ADD_AVATAR_SCREEN_MOUNTED_REQUEST });
      const userServiceClient = createUserServiceClient();
      let existingAvatar = MemoryImageCache.get(userId);
      if (!existingAvatar) {
        const result = await userServiceClient.getOtherUsersBase64Avatar(userId);
        if (result.base64Avatar) {
          existingAvatar = result.base64Avatar;
          MemoryImageCache.add(userId, existingAvatar);
        }
      }
      dispatch({ type: ADD_AVATAR_SCREEN_MOUNTED_SUCCESS, payload: { existingAvatar } });
    } catch (e) {
      dispatch({ type: ADD_AVATAR_SCREEN_MOUNTED_FAILURE, payload: { errorMessage: (e as Error).message } });
    }
  };
};

export const addAvatarWillUnmount = () => ({
  type: ADD_AVATAR_UNMOUNTED,
});

async function ensureCameraRollPermissionGranted() {
  if (Platform.OS === 'android' && Platform.Version >= 33) {
    // Android 13 (sdk 33) always has permission, based on manifest request
    return;
  }

  const cameraPermission = await Permissions.askAsync('cameraRoll');

  if (cameraPermission.status !== 'granted') {
    throw new Error(i18n.t('auth:appOnboardingAvatarNoCameraRollPermission'));
  }
}

export const choosePhotoPressed = (
  userId: string,
  hideActionPanel: () => void,
) => {
  return async (dispatch) => {
    try {
      await ensureCameraRollPermissionGranted();

      const camaraResponse = await launchImageLibrary(imagePickerOptions);
      hideActionPanel();

      dispatch({ type: ADD_AVATAR_REQUEST });
      const uploadedAvatar = getBase64ImageFromCameraResponse(camaraResponse);
      dispatch({ type: ADD_AVATAR_SUCCESS, payload: { uploadedAvatar } });
    } catch (e) {
      dispatch({ type: ADD_AVATAR_FAILURE, payload: { errorMessage: (e as Error).message } });
    }
  };
};

export const takePhotoPressed = (userId: string, hideActionPanel: () => void) => {
  return async (dispatch) => {
    try {
      const cameraPermission = await Permissions.askAsync('camera');

      if (cameraPermission.status !== 'granted') {
        throw new Error(i18n.t('auth:appOnboardingAvatarNoCameraPermission'));
      }

      const camaraResponse = await launchCamera(cameraOptions);
      hideActionPanel();

      dispatch({ type: ADD_AVATAR_REQUEST });
      const uploadedAvatar = getBase64ImageFromCameraResponse(camaraResponse);
      dispatch({ type: ADD_AVATAR_SUCCESS, payload: { uploadedAvatar } });
    } catch (e) {
      dispatch({ type: ADD_AVATAR_FAILURE, payload: { errorMessage: (e as Error).message } });
    }
  };
};

const navigateToNextOnBoardingStep = (dispatch, navigation) => {
  dispatch(getNextStep({
    currentScreen: SignupScreen.CaptureAvatar,
    userProfile: {},
  }));
  dispatch(navigateToNextStep(navigation));
};

const saveUserAvatar = (base64Image: string) => {
  const userProfileClient = createUserServiceClient();
  return userProfileClient.addUserBase64Avatar(base64Image);
};

export const nextPressed = (userId: string, navigation) => {
  return async (dispatch, getState: () => IAppStore) => {
    try {
      dispatch({ type: ADD_AVATAR_NEXT_PRESSED_REQUEST });
      const { uploadedAvatar } = getState().auth.signup;

      const userHasAvatar = MemoryImageCache.has(userId) || uploadedAvatar;
      if (!userHasAvatar) {
        throw new Error(i18n.t('auth:appOnboardingAvatarNextPressedWithoutAvatar'));
      }

      if (uploadedAvatar) {
        await saveUserAvatar(uploadedAvatar);
        MemoryImageCache.add(userId, uploadedAvatar);
      }

      navigateToNextOnBoardingStep(dispatch, navigation);
      dispatch({ type: ADD_AVATAR_NEXT_PRESSED_SUCCESS });
    } catch (e) {
      dispatch({ type: ADD_AVATAR_NEXT_PRESSED_FAILURE, payload: { errorMessage: (e as Error).message } });
    }
  };
};

export const skipPressed = (navigation) => {
  return async (dispatch) => {
    navigateToNextOnBoardingStep(dispatch, navigation);
  };
};

export const saveProfilePicturePressed = (userId: string, navigation) => {
  return async (dispatch, getState: () => IAppStore) => {
    try {
      dispatch({ type: SAVE_AVATAR_PRESS_REQUEST });
      const { uploadedAvatar } = getState().auth.signup;

      const userHasChangedPicture = !!uploadedAvatar;
      if (userHasChangedPicture) {
        await saveUserAvatar(uploadedAvatar);
        MemoryImageCache.add(userId, uploadedAvatar);
        showMessage(i18n.t('user:profilePicUploadSuccess'));
      }
      dispatch({ type: SAVE_AVATAR_PRESS_SUCCESS });
      navigation.goBack(null);
    } catch (e) {
      Logger.notify(e);
      dispatch({ type: SAVE_AVATAR_PRESS_FAILURE, payload: { errorMessage: (e as Error).message } });
    }
  };
};
